import React from 'react';
import { Box, HStack, Text, Image } from '@chakra-ui/react';
import { ScoreboardState } from '@/services/scoreboard';

export const ScoreboardPreview: React.FC<{ state: ScoreboardState }> = ({ state }) => {
  const theme = state.theme || 'pill';
  const isFlipped = !!state.sidesFlipped;
  const left = {
    short: (isFlipped ? state.awayShort : state.homeShort) || deriveShortLocal(isFlipped ? state.awayName : state.homeName),
    logo: isFlipped ? state.awayLogo : state.homeLogo,
    color: (isFlipped ? state.secondaryColor : state.primaryColor) || '#1e3a8a',
    score: isFlipped ? state.awayScore : state.homeScore,
    fouls: Math.max(0, Math.min(5, isFlipped ? (state.awayFouls || 0) : (state.homeFouls || 0))),
    name: isFlipped ? state.awayName : state.homeName,
    textColor: (isFlipped ? state.awayTextColor : state.homeTextColor) || '#ffffff',
  };
  const right = {
    short: (!isFlipped ? state.awayShort : state.homeShort) || deriveShortLocal(!isFlipped ? state.awayName : state.homeName),
    logo: !isFlipped ? state.awayLogo : state.homeLogo,
    color: (!isFlipped ? state.secondaryColor : state.primaryColor) || '#2563eb',
    score: !isFlipped ? state.awayScore : state.homeScore,
    fouls: Math.max(0, Math.min(5, !isFlipped ? (state.awayFouls || 0) : (state.homeFouls || 0))),
    name: !isFlipped ? state.awayName : state.homeName,
    textColor: (!isFlipped ? state.awayTextColor : state.homeTextColor) || '#ffffff',
  };
  const timer = state.timer || '00:00';

  switch (theme) {
    case 'pill':
      return (
        <Box maxW="100%" overflowX="auto">
          <HStack spacing={2} px={1.5} py={1} borderRadius="full" bg="white" borderWidth="1px" borderColor="gray.200" boxShadow="sm" width="max-content">
            <SegmentScore>{timer}</SegmentScore>
            <SegmentTeam colorA={left.color} textColor={left.textColor} left>
              {left.logo ? <Image src={left.logo} alt="home" boxSize="16px" objectFit="contain" /> : null}
              <Text textTransform="uppercase" fontSize="sm" lineHeight={1}>{left.short}</Text>
            </SegmentTeam>
            <SegmentScore>{left.score} – {right.score}</SegmentScore>
            <SegmentTeam colorA={right.color} textColor={right.textColor} right>
              <Text textTransform="uppercase" fontSize="sm" lineHeight={1}>{right.short}</Text>
              {right.logo ? <Image src={right.logo} alt="away" boxSize="16px" objectFit="contain" /> : null}
            </SegmentTeam>
          </HStack>
          <FoulsBar leftCount={left.fouls} rightCount={right.fouls} leftColor={left.color} rightColor={right.color} />
        </Box>
      );
    case 'classic':
    case 'var1':
      return (
        <Box>
          <HStack spacing={3} bgGradient="linear(to-b, #c8d4dc, #a8b8c4)" px={5} py={3} borderRadius="lg" boxShadow="md" width="max-content">
            <Box bg="white" color="black" fontWeight="bold" px={3} py={1} borderRadius="md" fontSize="lg">{timer}</Box>
            <Box bg={left.color} color="white" px={4} py={2} borderRadius="md" fontWeight="bold">{left.short}</Box>
            <Text fontWeight="bold" color="black">{left.score}-{right.score}</Text>
            <Box bg={right.color} color="white" px={4} py={2} borderRadius="md" fontWeight="bold">{right.short}</Box>
          </HStack>
          <FoulsBar leftCount={left.fouls} rightCount={right.fouls} leftColor={left.color} rightColor={right.color} />
        </Box>
      );
    case 'var2':
      return (
        <Box>
          <HStack spacing={0} borderRadius="md" overflow="hidden" boxShadow="md" width="max-content">
            <Box bgGradient="linear(135deg, #4a5568, #2d3748)" color="white" px={3} py={2} fontWeight="bold">{timer}</Box>
            <Box bgGradient="linear(135deg, #2c5282, #2a4365)" color="white" px={4} py={2} fontWeight="bold">{left.short}</Box>
            <Box bgGradient="linear(135deg, #2c5282, #2a4365)" color="white" px={3} py={2} fontWeight="bold">{left.score}-{right.score}</Box>
            <Box bgGradient="linear(135deg, #2c5282, #2a4365)" color="white" px={4} py={2} fontWeight="bold">{right.short}</Box>
          </HStack>
          <FoulsBar leftCount={left.fouls} rightCount={right.fouls} leftColor={left.color} rightColor={right.color} />
        </Box>
      );
    case 'var3':
      return (
        <Box textAlign="center" fontFamily="Poppins, Arial, sans-serif">
          <HStack spacing={0} justify="center">
            <Box w="102px" h="38px" bg="#F6F6F6" lineHeight="41px" position="relative">
              <Box position="absolute" left="-8px" top={0} w="6px" h="38px" bg={left.color} />
              <Text>{left.short}</Text>
            </Box>
            <Box w="102px" h="38px" bg="#F6F6F6" lineHeight="41px" zIndex={2} boxShadow="0 3px 10px rgba(0,0,0,0.7)">
              <Text fontWeight="bold">{left.score}-{right.score}</Text>
            </Box>
            <Box w="102px" h="38px" bg="#F6F6F6" lineHeight="41px" position="relative">
              <Box position="absolute" right="-8px" top={0} w="6px" h="38px" bg={right.color} />
              <Text>{right.short}</Text>
            </Box>
          </HStack>
          <Box mt={2} w="306px" mx="auto" bg="#F6F6F6">
            <Text>{timer}</Text>
          </Box>
          <FoulsBar leftCount={left.fouls} rightCount={right.fouls} leftColor={left.color} rightColor={right.color} />
        </Box>
      );
    case 'var4':
      return (
        <Box>
          <Box w="340px" borderWidth="1px" borderRadius="xl" boxShadow="xl" p={4} bg="white" color="gray.900">
            <HStack>
              <Text fontWeight="bold">{left.name}</Text>
              <Text ml="auto" fontWeight="extrabold">{left.score}</Text>
            </HStack>
            <Box textAlign="center" fontWeight="extrabold" py={1}>VS</Box>
            <HStack>
              <Text fontWeight="bold">{right.name}</Text>
              <Text ml="auto" fontWeight="extrabold">{right.score}</Text>
            </HStack>
            <HStack justify="flex-end" fontSize="sm" opacity={0.8} pt={2}>
              <Text>{timer}</Text>
            </HStack>
          </Box>
          <FoulsBar leftCount={left.fouls} rightCount={right.fouls} leftColor={left.color} rightColor={right.color} />
        </Box>
      );
    default:
      return (
        <Box>
          <HStack spacing={3} bg="gray.900" color="white" px={4} py={3} borderRadius="lg" boxShadow="lg" width="max-content">
            <Text fontWeight="bold">{left.name}</Text>
            <Text fontWeight="black">{left.score} : {right.score}</Text>
            <Text fontWeight="bold">{right.name}</Text>
          </HStack>
          <FoulsBar leftCount={left.fouls} rightCount={right.fouls} leftColor={left.color} rightColor={right.color} />
        </Box>
      );
  }
};

// Small presentational helpers for the pill theme
const SegmentTeam: React.FC<{ colorA?: string; textColor?: string; left?: boolean; right?: boolean; children: React.ReactNode }> = ({ colorA = '#1e3a8a', textColor = '#ffffff', left, right, children }) => {
  return (
    <HStack
      px={2}
      py={0.5}
      borderRadius="full"
      bgGradient={`linear(to-r, ${colorA}, ${shadeColor(colorA, 20)})`}
      color={textColor}
      spacing={1.5}
      minW="46px"
    >
      {children}
    </HStack>
  );
};

const SegmentScore: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box px={2} py={0.5} borderRadius="md" bg="gray.50" borderWidth="1px" borderColor="gray.200" fontWeight="800" minW="58px" textAlign="center" fontSize="sm">
      {children}
    </Box>
  );
};

const FoulsBar: React.FC<{ leftCount: number; rightCount: number; leftColor: string; rightColor: string }> = ({ leftCount, rightCount, leftColor, rightColor }) => {
  const Dot: React.FC<{ active: boolean; color: string }> = ({ active, color }) => (
    <Box w="8px" h="8px" borderRadius="full" bg={active ? color : 'gray.200'} borderWidth={active ? 0 : 1} borderColor="gray.300" />
  );
  return (
    <HStack spacing={6} justify="center" mt={2} width="100%">
      <HStack spacing={1}>
        {Array.from({ length: 5 }).map((_, i) => <Dot key={i} active={i < leftCount} color={leftColor} />)}
      </HStack>
      <HStack spacing={1}>
        {Array.from({ length: 5 }).map((_, i) => <Dot key={i} active={i < rightCount} color={rightColor} />)}
      </HStack>
    </HStack>
  );
};

function formatTimer(halfLength: number): string {
  const min = Math.max(0, Math.min(halfLength, 99));
  return `${String(min).padStart(2, '0')}:00`;
}

function deriveShortLocal(name?: string) {
  if (!name) return '---';
  const s = String(name).trim().toUpperCase();
  if (!s) return '---';
  const map: Record<string, string> = {
    'Á':'A','Ä':'A','Å':'A','Â':'A','À':'A',
    'Č':'C','Ć':'C','Ç':'C',
    'Ď':'D',
    'É':'E','Ě':'E','È':'E','Ë':'E','Ê':'E',
    'Í':'I','Ì':'I','Ï':'I','Î':'I',
    'Ň':'N','Ń':'N',
    'Ó':'O','Ö':'O','Ô':'O','Ò':'O',
    'Ř':'R',
    'Š':'S','Ś':'S',
    'Ť':'T',
    'Ú':'U','Ů':'U','Ù':'U','Ü':'U','Û':'U',
    'Ý':'Y',
    'Ž':'Z',
  };
  let out = '';
  for (const ch of s) {
    const c = map[ch] || ch;
    if (c >= 'A' && c <= 'Z') {
      out += c;
      if (out.length === 3) break;
    }
  }
  while (out.length < 3) out += '-';
  return out;
}

function shadeColor(hex: string, percent: number) {
  // Simple hex shade function
  try {
    const raw = hex.replace('#','');
    let n = raw;
    // Normalize to 6-digit RGB, ignore alpha channel if present
    if (n.length === 3 || n.length === 4) {
      // #RGB or #RGBA -> expand RGB, drop A
      const rgb = n.slice(0,3);
      n = rgb.split('').map((c)=>c+c).join('');
    } else if (n.length === 8) {
      // #RRGGBBAA -> drop AA
      n = n.slice(0,6);
    }
    if (n.length !== 6) return hex;
    const num = parseInt(n, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    r = Math.min(255, Math.max(0, Math.round(r + (percent/100)*255)));
    g = Math.min(255, Math.max(0, Math.round(g + (percent/100)*255)));
    b = Math.min(255, Math.max(0, Math.round(b + (percent/100)*255)));
    return `#${[r,g,b].map(v=>v.toString(16).padStart(2,'0')).join('')}`;
  } catch {
    return hex;
  }
}

export default React.memo(ScoreboardPreview);
